import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n";
import { registerSurveyBulk, getSurveysPaged } from "../../services/IntialAssessmentServices";

import "./style.css";
import { CiExport } from "react-icons/ci";
import { TfiImport } from "react-icons/tfi";
import { FiSearch } from "react-icons/fi";
import { toast } from "react-toastify";

import LoadingScreen from "src/Loading/LoadingScreen";
import { Modal } from "react-bootstrap";
import { ClipboardCheck } from "react-bootstrap-icons";
import { BiCategoryAlt } from "react-icons/bi";
import { FaQuestion } from "react-icons/fa";
import { useSearchQuery } from "src/hook/useSearchQuery";

interface QuestionCategory {
  id: string;
  en_name: string;
  ar_name?: string;
}

interface Survey {
  id: string;
  title: string;
  questionCategories: QuestionCategory[];
  createdAt?: string;
  updatedAt?: string;
}

type LoadingMode = "global" | "table";

const IntialAssessmentSup = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const modalFileInputRef = useRef<HTMLInputElement | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const didSkipFirstSearch = useRef<boolean>(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [modalSelectedFile, setModalSelectedFile] = useState<File | null>(null);

  const { searchQuery, searchResultType, clearSearchQuery } = useSearchQuery();

  const downloadExampleExcel = () => {
    const link = document.createElement("a");
    link.href = `/templates/Survey_Questions.xlsx`;
    link.download = "Survey_Questions_Template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportAndUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);

    try {
      const response = await registerSurveyBulk(formData);
      if (response?.success) {
        toast.success(String(t("surveys.excelImportedSuccess")));
        fetchSurveys("", page, "table");
        setShowImportModal(false);
      } else {
        toast.error(response.message || String(t("surveys.excelImportError")));
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(t("surveys.excelImportError"));
      toast.error(msg);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (modalFileInputRef.current) modalFileInputRef.current.value = "";
      setUploading(false);
    }
  };

  const closeImportModalIfIdle = (): void => {
    if (uploading) return;
    setShowImportModal(false);
    setModalSelectedFile(null);
  };

  const submitModalImportUpload = async (): Promise<void> => {
    if (!modalSelectedFile) {
      toast.error(t("error.no_file_selected"));
      return;
    }
    await handleImportAndUpload(modalSelectedFile);
    setModalSelectedFile(null);
  };

  const onModalUploadClick = (): void => {
    void submitModalImportUpload();
  };

  const fetchSurveys = async (query: string = "", pageNo: number = 1, loadingMode: LoadingMode = "table") => {
    if (loadingMode === "global") {
      setGlobalLoading(true);
    } else {
      setTableLoading(true);
    }
    try {
      const apiPageNo = Math.max(pageNo - 1, 0);
      const result = await getSurveysPaged(query, pageSize, apiPageNo);
      if (result) {
        setSurveys(result.data || []);
        setTotalPages(result.totalPages || 1);
      }
    } catch (err: unknown) {
      setError(true);
      setErrorMsg((err as Error).message || "An error occurred while fetching data.");
    } finally {
      if (loadingMode === "global") {
        setGlobalLoading(false);
      } else {
        setTableLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchSurveys("", page, "global");
    didSkipFirstSearch.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (didSkipFirstSearch.current) {
      didSkipFirstSearch.current = false;
      return;
    }
    const handler = setTimeout(() => {
      const q = (searchTerm || "").trim();
      if (q.length === 0) {
        setPage(1);
        fetchSurveys("", 1, "table");
      } else {
        setPage(1);
        fetchSurveys(q, 1, "table");
      }
    }, 1000);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (searchQuery && searchResultType === "assessment") {
      setSearchTerm(searchQuery);
      clearSearchQuery();
      setPage(1);
      fetchSurveys(searchQuery, 1, "table");
    }
  }, [searchQuery, searchResultType, clearSearchQuery]);

  if (uploading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
        <div>
          <LoadingScreen />
        </div>
      </div>
    );
  }

  if (globalLoading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>
        <div>
          <LoadingScreen />
        </div>
      </div>
    );
  }

  const totalLabel = Math.max(totalPages || 1, 1);

  return (
    <div className=" ia-sup-page">
     

      <div className="ia-sup-control-card">
        <div className="ia-sup-toolbar">
          <div className="ia-sup-search-wrap">
            <FiSearch className="ia-sup-search-icon" size={18} aria-hidden />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const q = (searchTerm || "").trim();
                  setPage(1);
                  fetchSurveys(q, 1, "table");
                }
              }}
              placeholder={String(t("surveys.search_placeholder"))}
              className="ia-sup-search-input"
              aria-label={String(t("surveys.search_placeholder"))}
            />
          </div>
          <div className="ia-sup-toolbar-actions" role="group">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleImportAndUpload(file);
                } else {
                  toast.error(t("error.no_file_selected"));
                }
              }}
              style={{ display: "none" }}
              id="import-excel-hidden"
            />
            <button type="button" className="ia-sup-btn ia-sup-btn-outline" onClick={() => setShowImportModal(true)}>
              <TfiImport size={15} aria-hidden />
              <span>{t("Standards_Page.import_excel")}</span>
            </button>
            <button type="button" className="ia-sup-btn ia-sup-btn-primary" onClick={downloadExampleExcel}>
              <CiExport size={16} aria-hidden />
              <span>{t("Standards_Page.download_excel_template")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="ia-sup-quick-panel">
        <p className="ia-sup-quick-label">
          {String(t("surveys.quick_links", { defaultValue: "Related setup" }))}
        </p>
        <div className="ia-sup-quick-grid">
          <button type="button" className="ia-sup-quick-card" onClick={() => navigate("/category")}>
            <span className="ia-sup-quick-icon" aria-hidden>
              <BiCategoryAlt size={22} />
            </span>
            <span className="ia-sup-quick-text">
              {String(t("surveys.quick_link_categories", { defaultValue: "Categories" }))}
            </span>
          </button>
          <button type="button" className="ia-sup-quick-card" onClick={() => navigate("/questionassessment")}>
            <span className="ia-sup-quick-icon" aria-hidden>
              <FaQuestion size={20} />
            </span>
            <span className="ia-sup-quick-text">
              {String(t("surveys.quick_link_questions", { defaultValue: "Questions" }))}
            </span>
          </button>
          <button
            type="button"
            className="ia-sup-quick-card"
            onClick={() => navigate("/SurveyCategoryQuestionManager")}
          >
            <span className="ia-sup-quick-icon" aria-hidden>
              <ClipboardCheck size={22} />
            </span>
            <span className="ia-sup-quick-text">
              {String(t("surveys.quick_link_survey_categories", { defaultValue: "Survey categories" }))}
            </span>
          </button>
        </div>
      </div>

      {error ? (
        <div className="ia-sup-alert alert alert-danger" role="alert">
          {errorMsg}
        </div>
      ) : null}

      <div className="ia-sup-table-panel">
        <div className="table-responsive">
          <table className="ia-sup-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{t("surveys.title")}</th>
                <th scope="col">{t("surveys.categories")}</th>
                <th scope="col" className="ia-sup-th-narrow">
                  {t("surveys.category_count")}
                </th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan={4}>
                    <div className="ia-sup-table-loading">
                      <div className="spinner-border ia-sup-spinner" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : surveys.length === 0 ? (
                <tr>
                  <td colSpan={4} className="ia-sup-empty-cell">
                    {t("surveys.no_surveys")}
                  </td>
                </tr>
              ) : (
                surveys.map((item, index) => (
                  <tr key={item.id}>
                    <td className="ia-sup-td-index">{(page - 1) * pageSize + index + 1}</td>
                    <td className="ia-sup-td-title">{item.title}</td>
                    <td>
                      {item?.questionCategories && item?.questionCategories?.length > 0 ? (
                        <ul className="ia-sup-cat-list">
                          {item.questionCategories.map((cat) => (
                            <li key={cat.id}>
                              {i18n.language === "ar" ? cat.ar_name || cat.en_name : cat.en_name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-muted">{t("surveys.no_categories")}</span>
                      )}
                    </td>
                    <td className="ia-sup-td-count">{item.questionCategories?.length || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <nav className="ia-sup-pagination" aria-label={String(t("surveys.page"))}>
        <button
          type="button"
          className="ia-sup-page-btn"
          onClick={() => {
            setPage(1);
            fetchSurveys(searchTerm, 1, "table");
          }}
          disabled={page === 1}
        >
          «
        </button>
        <button
          type="button"
          className="ia-sup-page-btn"
          onClick={() => {
            const p = Math.max(page - 1, 1);
            setPage(p);
            fetchSurveys(searchTerm, p, "table");
          }}
          disabled={page === 1}
        >
          ‹ {t("institution.pagination_previous")}
        </button>
        <span className="ia-sup-page-current" aria-current="page">
          {page}
        </span>
        <button
          type="button"
          className="ia-sup-page-btn"
          onClick={() => {
            const p = Math.min(page + 1, totalLabel);
            setPage(p);
            fetchSurveys(searchTerm, p, "table");
          }}
          disabled={page >= totalLabel}
        >
          {t("institution.pagination_next")} ›
        </button>
        <button
          type="button"
          className="ia-sup-page-btn"
          onClick={() => {
            const p = totalLabel;
            setPage(p);
            fetchSurveys(searchTerm, p, "table");
          }}
          disabled={page >= totalLabel}
        >
          »
        </button>
        <span className="ia-sup-page-meta">
          {t("surveys.page")} {page} {t("surveys.to")} {totalLabel}
        </span>
      </nav>

      <Modal show={showImportModal} onHide={closeImportModalIfIdle} centered className="ia-sup-import-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t("import_modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>{t("import_modal.instructions_title")}</strong>
            <ul className="mt-2 ia-sup-modal-list">
              <li>{t("import_modal.accepted_files")}</li>
              <li>{t("import_modal.required_columns")}</li>
              <li>{t("import_modal.carry_forward")}</li>
              <li>{t("import_modal.categories_global")}</li>
              <li>{t("import_modal.add_question")}</li>
              <li>{t("import_modal.update_question")}</li>
              <li>{t("import_modal.choice_options")}</li>
              <li>{t("import_modal.numeric_date_rules")}</li>
              <li>{t("import_modal.arabic_fallback")}</li>
              <li>{t("import_modal.missing_columns")}</li>
            </ul>
          </div>

          <div className="ia-sup-upload-zone">
            <div className="mb-2">
              <strong>{t("import_modal.upload_section")}</strong>
            </div>
            <input
              ref={modalFileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="form-control form-control-sm"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setModalSelectedFile(file);
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="ia-sup-modal-footer">
          <button type="button" className="ia-sup-btn ia-sup-btn-muted" onClick={closeImportModalIfIdle} disabled={uploading}>
            {t("import_modal.close")}
          </button>
          <button
            type="button"
            className="ia-sup-btn ia-sup-btn-primary"
            onClick={onModalUploadClick}
            disabled={uploading || !modalSelectedFile}
          >
            {uploading ? t("import_modal.uploading") : t("import_modal.upload")}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default IntialAssessmentSup;
